/**
 * 訪談新增 / 編輯共用 inline form。
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  CreateInterviewSchema,
  type CreateInterviewInput,
  type InterviewResponse,
} from '@app/shared';
import { useInterviewTypes } from './api';

export interface InterviewFormProps {
  defaultValues?: Partial<InterviewResponse>;
  submitting?: boolean;
  onSubmit: (values: CreateInterviewInput) => void;
  onCancel?: () => void;
  submitLabel?: string;
  /**
   * REQ-20260810002:是否顯示「業務私帳」勾選框。
   *
   * **只有客戶訪談有**。這張表單同時服務廠商訪談,而 `SupplierInterview` 沒有
   * `isPrivateAccount` 欄位(廠商沒有應收概念)—— 送過去 Prisma 會直接報未知欄位。
   * 連 `defaultValues` 都要一起省略:RHF 的 `handleSubmit` 會把沒 register 的
   * 預設值也一起交出來。
   */
  showPrivateAccount?: boolean;
  /**
   * REQ-20260825009:送出鍵是否依「表單有沒有異動」變色(未異動時同取消鍵的灰框)。
   * 只有編輯訪談會開;新增訪談本來就是要送出的,維持固定藍色。
   */
  colorByDirty?: boolean;
}

export function InterviewForm({
  defaultValues,
  submitting,
  onSubmit,
  onCancel,
  submitLabel = '儲存',
  showPrivateAccount = false,
  colorByDirty = false,
}: InterviewFormProps): JSX.Element {
  const typesQuery = useInterviewTypes();

  // visitedAt 在 RHF state 以 date input 的本地格式(YYYY-MM-DD)存放,
  // 因為 <input type="date"> 不接受 ISO-Z;setValueAs 會在 submit 時把使用者
  // 挑的「當地日期」當天 00:00(local)轉回 ISO,符合台灣業務一律單一時區的語意。
  const initialVisitedAtLocal = toLocalDateInputValue(defaultValues?.visitedAt);
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CreateInterviewInput>({
    resolver: zodResolver(CreateInterviewSchema),
    defaultValues: {
      interviewTypeId: defaultValues?.interviewTypeId ?? null,
      contact: defaultValues?.contact ?? null,
      // 型別上是 ISO string | null,這裡暫存 local format;RHF 顯示時需此格式,
      // setValueAs 在 submit 時會轉 ISO → zod `.datetime()` 驗證通過。
      visitedAt: (initialVisitedAtLocal || null) as string | null,
      notes: defaultValues?.notes ?? null,
      // REQ-20260810002:私帳標籤。編輯時沿用該筆訪談的值,新增預設不勾。
      // 廠商訪談完全不帶這個欄位(見 `showPrivateAccount` 的說明)。
      ...(showPrivateAccount
        ? { isPrivateAccount: defaultValues?.isPrivateAccount ?? false }
        : {}),
    },
  });

  const errorMessages = Object.entries(errors)
    .map(([key, val]) => [key, (val as { message?: string } | undefined)?.message])
    .filter((x): x is [string, string] => typeof x[1] === 'string');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded-md p-3">
      {errorMessages.length > 0 ? (
        <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
          {errorMessages.map(([k, m]) => (
            <div key={k}>
              {k}: {m}
            </div>
          ))}
        </div>
      ) : null}
      <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
        <label className="grid gap-1">
          <span className="text-sm text-slate-700">訪談類別</span>
          <select
            {...register('interviewTypeId', {
              setValueAs: (v) => (v === '' || v == null ? null : Number(v)),
            })}
            className={inputCls}
            defaultValue={defaultValues?.interviewTypeId ?? ''}
          >
            <option value="">（未分類）</option>
            {typesQuery.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>

        <label className="grid gap-1">
          <span className="text-sm text-slate-700">聯絡人</span>
          <input
            type="text"
            {...register('contact', { setValueAs: (v) => (v === '' ? null : v) })}
            className={inputCls}
          />
        </label>

        <label className="grid gap-1">
          <span className="text-sm text-slate-700">拜訪日期</span>
          <input
            type="date"
            // 點輸入框任何位置都會跳日曆;有 showPicker 的瀏覽器(Chrome/Edge/Safari 16+)生效,
            // 其它瀏覽器點輸入框本身也會自動開,所以失敗時就 fall back 預設行為。
            onClick={(e) => {
              const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
              try {
                el.showPicker?.();
              } catch {
                /* showPicker 在某些情境會 throw,直接忽略走預設行為 */
              }
            }}
            {...register('visitedAt', {
              // 把 YYYY-MM-DD 當作「使用者所在時區的當天 00:00」存成 ISO,
              // 顯示時用 toLocaleDateString 還原成同一天(單一時區業務不會出問題)。
              setValueAs: (v) => {
                if (v === '' || v == null) return null;
                const parts = String(v).split('-').map((p) => Number(p));
                const [y, m, d] = parts;
                if (!y || !m || !d) return null;
                const dt = new Date(y, m - 1, d);
                return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
              },
            })}
            className={inputCls}
          />
        </label>
      </div>

      {/*
        REQ-20260810002:業務私帳。checkbox 的值本來就是 boolean,
        直接 register 即可(不像 radio 那樣一律回傳字串)。
        建應收單時選「個人」也會由後端自動打勾,這裡是手動標記 / 手動取消的入口。
      */}
      {showPrivateAccount ? (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            {...register('isPrivateAccount')}
            data-testid="interview-private-account"
          />
          <span className="text-sm text-slate-700">
            👤 業務私帳(帳款對象是個人,不列入公司月結對帳單)
          </span>
        </label>
      ) : null}

      <label className="grid gap-1">
        <span className="text-sm text-slate-700">Notes</span>
        <textarea
          {...register('notes', { setValueAs: (v) => (v === '' ? null : v) })}
          className={inputCls}
          rows={4}
        />
        {errors.notes ? (
          <span role="alert" className="text-sm text-red-600">
            {errors.notes.message}
          </span>
        ) : null}
      </label>

      <div className="flex gap-2 justify-center">
        <button
          type="submit"
          disabled={submitting}
          className={
            colorByDirty && !isDirty
              ? 'border rounded px-3 py-1.5 hover:bg-slate-100 disabled:opacity-50'
              : 'bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50'
          }
        >
          {submitting ? '儲存中…' : submitLabel}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="border rounded px-3 py-1.5 hover:bg-slate-100"
          >
            取消
          </button>
        ) : null}
      </div>
    </form>
  );
}

const inputCls =
  'w-full border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';

/**
 * 把 ISO 字串轉成 date input 所需的 `YYYY-MM-DD` 本地時區格式。
 * 用 toISOString().slice(0,10) 會得到 UTC 日期,跨日凌晨會差一天(Asia/Taipei)。
 */
function toLocalDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
